/** The card shows one step at a time, and the visible step owns the busy state
 * and the error banner.
 *
 * That single invariant used to be spread across four functions in the flow —
 * `showStep`, `visibleStep`, `busy` and `fail` — three of which re-derived
 * "which section is showing" before doing their real work, and each of which
 * had to remember the others' half of the rule. Naming it leaves the flow
 * saying which step to show and what to do on arrival, never how a step is
 * shown.
 */

import type { CardHeader, ErrorBanner } from "../components/card";
import { clearBusy, startBusy } from "../components/form";

/** The steps of the sign-in flow, in the order the visitor meets them —
 * `specs/glossary.md`'s entry step → code step → registration step (new
 * visitors) / consent step. */
export type StepName = "entry" | "code" | "registration" | "consent";

export interface StepView {
  /** Show exactly this step: every other section hidden, the card titled for
   * it, the busy state cleared and any error dismissed. Returns the
   * now-visible section, so the caller can focus into it. */
  show(name: StepName): HTMLElement;

  /** Put the visible step into its loading state while `form` submits. */
  busy(form: HTMLFormElement): void;

  /** Say what's wrong with what the visitor typed. No submit was in flight —
   * the step never left its idle state — so only the message changes. */
  reject(message: string): void;

  /** Leave the loading state and say what went wrong: `err.message` when it
   * carries one — the `api/` layer throws messages that are safe to show —
   * else `fallback`. */
  fail(err: unknown, fallback: string): void;
}

export function createStepView(opts: {
  sections: Record<StepName, HTMLElement>;
  header: CardHeader;
  error: ErrorBanner;
  /** The card title for each step. */
  titleFor(name: StepName): string;
}): StepView {
  const { sections, header, error, titleFor } = opts;
  // The card opens on the entry step — the one section built without `hidden`.
  // Deliberately not established by calling show("entry") here: show() clears
  // the busy state, and clearBusy re-enables every control in the section,
  // which would un-gate a CTA that must start disabled.
  let visible = sections.entry;

  function show(name: StepName): HTMLElement {
    for (const [step, section] of Object.entries(sections)) {
      section.hidden = step !== name;
    }
    visible = sections[name];
    header.setTitle(titleFor(name));
    clearBusy(visible);
    error.hide();
    return visible;
  }

  function reject(message: string): void {
    error.show(message);
  }

  return {
    show,
    reject,
    busy(form) {
      startBusy(visible, form);
    },
    fail(err, fallback) {
      clearBusy(visible);
      reject(err instanceof Error && err.message ? err.message : fallback);
    },
  };
}
